import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { usePresence } from '../hooks/usePresence'
import { menuSize, placeMenu } from '../utils/menuPlacement'
import {
  describeSearchPanelState,
  flattenSearchRows,
  partitionSearchResults,
  searchErrorMessage,
  uniqueSavedItems,
} from '../utils/semanticSearchPolicy'
import { useProductCardImage } from '../hooks/useProductCardImage'
import './SemanticSearchPanel.css'
import '../styles/scrollbar.css'

function ResultRow({ item, active, onSelect, onHover, formatPrice, cartLabel }) {
  const { src, onError } = useProductCardImage(item)
  return (
    <button
      type="button"
      role="option"
      className={`semantic-search-item${active ? ' is-active' : ''}`}
      aria-selected={active}
      onMouseEnter={onHover}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onSelect?.(item)}
    >
      <img
        src={src || '/Icon photo size select actual.png'}
        alt=""
        className="semantic-search-thumb"
        onError={src ? onError : undefined}
      />
      <span className="semantic-search-copy">
        <span className="semantic-search-name">{item.name || 'Produit'}</span>
        <span className="semantic-search-meta">
          <span className="semantic-search-price">{formatPrice?.(item.price)}</span>
          {cartLabel ? <span className="semantic-search-cart">{cartLabel}</span> : null}
        </span>
      </span>
    </button>
  )
}

export default function SemanticSearchPanel({
  open = false,
  anchorRef,
  query = '',
  isLoading = false,
  isFetching = false,
  isError = false,
  error = null,
  items = [],
  savedItems = [],
  currentCartId = null,
  formatPrice,
  onClose,
  onSelect,
  onImageError,
}) {
  const panelRef = useRef(null)
  const reducedMotion = usePrefersReducedMotion()
  const { mounted, leaving } = usePresence(open, { duration: 180, reduced: reducedMotion })
  const [coords, setCoords] = useState({ left: 0, top: 0, width: 360 })
  const [activeIndex, setActiveIndex] = useState(0)

  const partitioned = useMemo(
    () => partitionSearchResults(items, currentCartId),
    [items, currentCartId],
  )
  const saved = useMemo(() => uniqueSavedItems(savedItems), [savedItems])
  const rows = useMemo(
    () => flattenSearchRows({ current: partitioned.current, others: partitioned.others, saved }),
    [partitioned.current, partitioned.others, saved],
  )
  const state = describeSearchPanelState({
    query,
    isLoading,
    isFetching,
    isError,
    items,
    savedItems: saved,
  })

  useLayoutEffect(() => {
    if (!mounted) return undefined
    const update = () => {
      const anchor = anchorRef?.current
      const rect = anchor?.getBoundingClientRect?.()
      const size = menuSize(panelRef.current)
      const width = Math.min(440, Math.max(rect?.width || 320, 280), Math.max(160, window.innerWidth - 24))
      const placed = placeMenu({
        x: rect?.left || 12,
        y: (rect?.bottom || 48) + 8,
        width,
        height: size.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
      })
      const next = { ...placed, width }
      setCoords((prev) => (
        prev.left === next.left && prev.top === next.top && prev.width === next.width
          ? prev
          : next
      ))
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [mounted, anchorRef, rows.length, state, query])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, items, savedItems])

  useEffect(() => {
    if (!mounted) return undefined
    const onPointer = (event) => {
      if (panelRef.current?.contains(event.target) || anchorRef?.current?.contains?.(event.target)) return
      onClose?.()
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
        return
      }
      if (!rows.length) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => (index + 1) % rows.length)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => (index - 1 + rows.length) % rows.length)
      } else if (event.key === 'Home') {
        event.preventDefault()
        setActiveIndex(0)
      } else if (event.key === 'End') {
        event.preventDefault()
        setActiveIndex(rows.length - 1)
      } else if (event.key === 'Enter' && rows[activeIndex]) {
        event.preventDefault()
        onSelect?.(rows[activeIndex].item)
      }
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [mounted, rows, activeIndex, onClose, onSelect, anchorRef])

  if (!mounted || typeof document === 'undefined') return null

  const groups = [
    {
      title: partitioned.showGroups ? 'Dans ce panier' : null,
      items: partitioned.current,
    },
    {
      title: partitioned.showGroups ? 'Dans d’autres paniers' : null,
      items: partitioned.others,
    },
    {
      title: saved.length ? 'Articles sauvegardés' : null,
      items: saved,
    },
  ].filter((group) => group.items.length > 0)

  let nextIndex = 0
  const numberedGroups = groups.map((group) => {
    const startIndex = nextIndex
    nextIndex += group.items.length
    return { ...group, startIndex }
  })

  return createPortal(
    <div
      className="semantic-search-root"
      style={{ left: coords.left, top: coords.top }}
      data-testid="semantic-search-panel"
    >
      <div className="lg-blur-prewarm" aria-hidden="true" />
      <div
        ref={panelRef}
        className={`lg-panel semantic-search-panel${reducedMotion ? '' : leaving ? ' is-leaving' : ' is-animated'}`}
        id="semantic-search-panel"
        role="listbox"
        aria-label="Résultats de recherche"
        style={{ width: coords.width }}
      >
        <div className="lg-panel-frost" />
        <div className="lg-panel-tint" />
        <div className="lg-panel-shine" />
        <div className="lg-panel-body">
          <div className="lg-scroll semantic-search-scroll">
            {state === 'loading' ? (
              <p className="semantic-search-status">Recherche sémantique en cours…</p>
            ) : null}
            {state === 'error' ? (
              <p className="semantic-search-error" role="alert">{searchErrorMessage(error)}</p>
            ) : null}
            {state === 'empty' ? (
              <p className="semantic-search-empty">Aucun produit ne correspond à cette recherche.</p>
            ) : null}
            {state === 'ready' ? numberedGroups.map((group) => (
              <section key={group.title || group.items[0]?.id} className="semantic-search-section">
                {group.title ? <h3 className="semantic-search-section-title">{group.title}</h3> : null}
                {group.items.map((item, offset) => (
                  <ResultRow
                    key={item.id}
                    item={item}
                    active={group.startIndex + offset === activeIndex}
                    formatPrice={formatPrice}
                    onImageError={onImageError}
                    cartLabel={item.cart_name ? `Panier · ${item.cart_name}` : (group.title === 'Articles sauvegardés' ? 'Bibliothèque' : null)}
                    onHover={() => setActiveIndex(group.startIndex + offset)}
                    onSelect={onSelect}
                  />
                ))}
              </section>
            )) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
